import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Plus, MagnifyingGlass, Phone, EnvelopeSimple, Buildings, PencilSimple, Trash, ArrowRight, IdentificationCard, CheckCircle, Warning, UserPlus, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import FicheEntrepriseModal from '../components/entreprise/FicheEntrepriseModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Entreprises() {
  const { isAdmin } = useAuth();
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntreprise, setEditingEntreprise] = useState(null);
  const [originalEmail, setOriginalEmail] = useState('');
  const [newEntreprise, setNewEntreprise] = useState({ nom: '', email: '', telephone: '', siret: '', activite: '', contact_principal: '' });
  const [createAccount, setCreateAccount] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [ficheId, setFicheId] = useState(null);
  const [ficheOpen, setFicheOpen] = useState(false);

  useEffect(() => {
    fetchEntreprises();
  }, []);

  const fetchEntreprises = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/entreprises`, { withCredentials: true });
      setEntreprises(data);
    } catch (err) {
      console.error('Error fetching entreprises:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendAccess = async (entreprise) => {
    if (!entreprise.user_id) { toast.error("Pas de compte lié à cette entreprise"); return; }
    if (!window.confirm(
      `Renvoyer l'invitation à ${entreprise.email} ?\n\n` +
      `L'entreprise recevra un lien pour choisir son mot de passe (son mot de passe actuel reste valable tant que le lien n'est pas utilisé).`
    )) return;
    setBusyId(entreprise.id);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/users/${entreprise.user_id}/resend-invitation`, {}, { withCredentials: true });
      if (data?.activation_url) window.prompt("E-mail non envoyé. Lien d'invitation à transmettre :", data.activation_url);
      else toast.success(data?.message || `Invitation renvoyée à ${entreprise.email}`);
      await fetchEntreprises();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateAccountForEntreprise = async (entreprise) => {
    if (!entreprise.email) { toast.error("Cette entreprise n'a pas d'email — impossible de créer un compte"); return; }
    if (!window.confirm(
      `Créer un compte utilisateur pour "${entreprise.nom}" ?\n\n` +
      `Email : ${entreprise.email}\nRôle : entreprise\n\n` +
      `Une invitation avec un lien pour choisir son mot de passe sera envoyée par e-mail.`
    )) return;
    setBusyId(entreprise.id);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/entreprises/${entreprise.id}/create-account`, {}, { withCredentials: true });
      if (data?.activation_url) window.prompt("Compte créé, mais l'e-mail n'est pas parti. Lien d'invitation à transmettre :", data.activation_url);
      else toast.success(data?.message || `Compte créé et invitation envoyée à ${entreprise.email}`);
      await fetchEntreprises();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(`Un compte existe déjà avec l'email ${entreprise.email}`);
      } else {
        toast.error(e?.response?.data?.detail || 'Erreur création compte');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleAddEntreprise = async (e) => {
    e.preventDefault();
    if (!newEntreprise.nom.trim()) { toast.error('Nom requis'); return; }
    try {
      const willCreateAccount = createAccount && !!newEntreprise.email.trim();
      const { data } = await axios.post(
        `${API_URL}/api/entreprises`,
        { ...newEntreprise, create_account: createAccount },
        { withCredentials: true }
      );
      if (data?.account_created) {
        toast.success(
          `${newEntreprise.nom} ajoutée à l'annuaire\nCompte créé · email d'accès envoyé à ${newEntreprise.email}`,
          { duration: 8000 }
        );
      } else if (willCreateAccount && data?.user_id && !data?.account_created) {
        toast.success(`${newEntreprise.nom} ajoutée — compte déjà existant rattaché`);
      } else {
        toast.success(`${newEntreprise.nom} ajoutée à l'annuaire`);
      }
      setAddDialogOpen(false);
      setNewEntreprise({ nom: '', email: '', telephone: '', siret: '', activite: '', contact_principal: '' });
      setCreateAccount(true);
      fetchEntreprises();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleEditEntreprise = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.put(`${API_URL}/api/entreprises/${editingEntreprise.id}`, {
        nom: editingEntreprise.nom,
        email: editingEntreprise.email,
        telephone: editingEntreprise.telephone || '',
        siret: editingEntreprise.siret || '',
        activite: editingEntreprise.activite || '',
        contact_principal: editingEntreprise.contact_principal || '',
      }, { withCredentials: true });

      // Toast adaptatif selon ce qui a été synchronisé (Iter 85)
      const summary = data?.summary || {};
      let message = 'Entreprise mise à jour';
      const details = [];
      if (summary.lots_synced > 0) details.push(`${summary.lots_synced} lot(s) sync`);
      if (summary.user_synced) details.push('compte sync');
      if (summary.email_changed) details.push('email de connexion changé');
      if (summary.invitations_synced > 0) details.push(`${summary.invitations_synced} invitation(s) à un appel d'offres`);
      if (details.length) message += ` — ${details.join(', ')}`;
      toast.success(message);

      setEditDialogOpen(false);
      setEditingEntreprise(null);
      fetchEntreprises();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || `Cet email est déjà utilisé par un autre compte`);
      } else {
        toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
      }
    }
  };

  const handleDeleteEntreprise = async (ent) => {
    if (!window.confirm(`Supprimer "${ent.nom}" de l'annuaire ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API_URL}/api/entreprises/${ent.id}`, { withCredentials: true });
      toast.success('Entreprise supprimée');
      fetchEntreprises();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openFiche = (id) => { setFicheId(id); setFicheOpen(true); };

  const filteredEntreprises = entreprises.filter(e =>
    (e.nom || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.activite || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.siret || '').includes(search)
  );

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="entreprises-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Annuaire entreprises
            </h1>
            <p className="text-[#6B6B6B] mt-1">
              {entreprises.length} entreprise{entreprises.length > 1 ? 's' : ''} — cliquez sur une fiche pour voir tous les détails
            </p>
          </div>
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" data-testid="add-entreprise-button">
                <Plus size={18} weight="bold" />
                Nouvelle entreprise
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Ajouter à l'annuaire</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddEntreprise} className="space-y-4">
                <div>
                  <Label>Nom de l'entreprise *</Label>
                  <Input value={newEntreprise.nom} onChange={e => setNewEntreprise({ ...newEntreprise, nom: e.target.value })} placeholder="Bâti Pro SARL" required className="rounded-none" data-testid="new-entreprise-nom" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>SIRET</Label>
                    <Input value={newEntreprise.siret} onChange={e => setNewEntreprise({ ...newEntreprise, siret: e.target.value })} placeholder="123 456 789 00012" className="rounded-none" />
                  </div>
                  <div>
                    <Label>Activité</Label>
                    <Input value={newEntreprise.activite} onChange={e => setNewEntreprise({ ...newEntreprise, activite: e.target.value })} placeholder="Gros Œuvre" className="rounded-none" />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={newEntreprise.email} onChange={e => setNewEntreprise({ ...newEntreprise, email: e.target.value })} placeholder="contact@batipro.fr" className="rounded-none" data-testid="new-entreprise-email" />
                </div>
                <div className="flex items-start gap-2 bg-blue-50 border-l-4 border-blue-500 p-3" data-testid="create-account-block">
                  <input
                    type="checkbox"
                    id="create_account"
                    checked={createAccount && !!newEntreprise.email.trim()}
                    onChange={e => setCreateAccount(e.target.checked)}
                    disabled={!newEntreprise.email.trim()}
                    className="mt-1"
                    data-testid="create-account-checkbox"
                  />
                  <label htmlFor="create_account" className="text-sm flex-1 cursor-pointer">
                    <span className="font-semibold text-[#2C2C2C]">Créer un compte et envoyer les accès</span>
                    <p className="text-xs text-[#6B6B6B] mt-1">
                      L&apos;entreprise recevra une invitation par e-mail avec un lien pour choisir son mot de passe (appels d&apos;offres, actions sur ses chantiers).
                    </p>
                    {!newEntreprise.email.trim() && (
                      <p className="text-xs text-[#8B3A3A] mt-1" data-testid="create-account-email-required">
                        Renseignez l&apos;email pour activer cette option.
                      </p>
                    )}
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Téléphone</Label>
                    <Input value={newEntreprise.telephone} onChange={e => setNewEntreprise({ ...newEntreprise, telephone: e.target.value })} placeholder="01 23 45 67 89" className="rounded-none" />
                  </div>
                  <div>
                    <Label>Contact principal</Label>
                    <Input value={newEntreprise.contact_principal} onChange={e => setNewEntreprise({ ...newEntreprise, contact_principal: e.target.value })} placeholder="M. Dupont" className="rounded-none" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-none">Annuler</Button>
                  <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Ajouter</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <Input
              placeholder="Rechercher une entreprise..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-none border-[#E0E0DB]"
              data-testid="entreprise-search"
            />
          </div>
        </div>

        {/* Entreprises Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="card-kroquis animate-pulse"><div className="h-32 bg-[#F5F5F3]" /></div>
            ))}
          </div>
        ) : filteredEntreprises.length === 0 ? (
          <div className="card-kroquis text-center py-16">
            <Buildings size={64} className="mx-auto text-[#E0E0DB] mb-4" />
            <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucune entreprise</h3>
            <p className="text-[#6B6B6B]">Ajoutez des entreprises ou invitez-les à un appel d'offres pour les voir apparaître ici</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEntreprises.map(entreprise => (
              <div
                key={entreprise.id}
                className="card-kroquis cursor-pointer hover:border-[#C75B39] transition-colors group"
                data-testid={`entreprise-card-${entreprise.id}`}
                onClick={() => openFiche(entreprise.id)}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#F5F5F3] flex items-center justify-center text-[#C75B39]">
                    <Buildings size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#2C2C2C] truncate">{entreprise.nom}</h3>
                    <p className="text-sm text-[#C75B39] font-medium">{entreprise.activite || 'Entreprise'}</p>
                    {entreprise.user_id ? (
                      <div className="flex items-center gap-1 mt-1" data-testid={`entreprise-account-status-${entreprise.id}`}>
                        <CheckCircle size={12} className="text-emerald-600" weight="fill" />
                        <span className="text-[10px] text-emerald-700 uppercase tracking-wide">
                          Compte actif
                          {entreprise.welcome_email_sent_at && (
                            <span className="text-[#9A9A9A] normal-case tracking-normal ml-1">
                              — envoyé {new Date(entreprise.welcome_email_sent_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : entreprise.email ? (
                      <div className="flex items-center gap-1 mt-1" data-testid={`entreprise-account-status-${entreprise.id}`}>
                        <Warning size={12} className="text-amber-600" weight="fill" />
                        <span className="text-[10px] text-amber-700 uppercase tracking-wide">Sans compte</span>
                      </div>
                    ) : null}
                  </div>
                  <ArrowRight size={18} className="text-[#9A9A9A] group-hover:text-[#C75B39] transition-colors" />
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  {entreprise.siret && (
                    <div className="flex items-center gap-2 text-[#6B6B6B]">
                      <IdentificationCard size={14} />
                      <span className="text-xs">{entreprise.siret}</span>
                    </div>
                  )}
                  {entreprise.telephone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-[#6B6B6B]" />
                      <span>{entreprise.telephone}</span>
                    </div>
                  )}
                  {entreprise.email && (
                    <div className="flex items-center gap-2">
                      <EnvelopeSimple size={14} className="text-[#6B6B6B]" />
                      <span className="text-[#1E3A5F] truncate">{entreprise.email}</span>
                    </div>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2 pt-4 border-t border-[#E0E0DB]" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none gap-1 flex-1"
                      onClick={() => { setEditingEntreprise({ ...entreprise }); setOriginalEmail(entreprise.email || ''); setEditDialogOpen(true); }}
                      data-testid={`edit-entreprise-${entreprise.id}`}
                    >
                      <PencilSimple size={14} /> Modifier
                    </Button>
                    {entreprise.user_id ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none gap-1 flex-1 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                        onClick={() => handleResendAccess(entreprise)}
                        disabled={busyId === entreprise.id || !entreprise.email}
                        title={entreprise.email ? `Renvoyer l'invitation à ${entreprise.email}` : "Pas d'e-mail renseigné"}
                        data-testid={`resend-access-${entreprise.id}`}
                      >
                        <PaperPlaneTilt size={14} weight="bold" />
                        {busyId === entreprise.id ? '...' : 'Renvoyer'}
                      </Button>
                    ) : entreprise.email ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none gap-1 flex-1 border-[#C75B39] text-[#C75B39] hover:bg-[#C75B39] hover:text-white"
                        onClick={() => handleCreateAccountForEntreprise(entreprise)}
                        disabled={busyId === entreprise.id}
                        title={`Créer compte et envoyer accès à ${entreprise.email}`}
                        data-testid={`create-account-${entreprise.id}`}
                      >
                        <UserPlus size={14} weight="bold" />
                        {busyId === entreprise.id ? '...' : 'Créer'}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="rounded-none gap-1 flex-1 opacity-50 cursor-not-allowed"
                        title="Pas d'email renseigné. Modifiez l'entreprise pour ajouter un email."
                        data-testid={`no-email-${entreprise.id}`}
                      >
                        <EnvelopeSimple size={14} /> Sans email
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none gap-1 text-[#8B3A3A] hover:bg-red-50"
                      onClick={() => handleDeleteEntreprise(entreprise)}
                      data-testid={`delete-entreprise-${entreprise.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier l'entreprise</DialogTitle>
            </DialogHeader>
            {editingEntreprise && (
              <form onSubmit={handleEditEntreprise} className="space-y-4">
                <div>
                  <Label>Nom</Label>
                  <Input value={editingEntreprise.nom || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, nom: e.target.value })} required className="rounded-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>SIRET</Label>
                    <Input value={editingEntreprise.siret || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, siret: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label>Activité</Label>
                    <Input value={editingEntreprise.activite || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, activite: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editingEntreprise.email || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, email: e.target.value })} className="rounded-none" data-testid="edit-entreprise-email" />
                  {originalEmail && editingEntreprise.email && editingEntreprise.email.trim().toLowerCase() !== originalEmail.trim().toLowerCase() && editingEntreprise.user_id && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 mt-1" data-testid="edit-email-warning">
                      <p className="font-semibold">⚠ L&apos;email du compte de connexion sera également modifié.</p>
                      <p className="text-amber-600 mt-1">
                        {editingEntreprise.nom} devra désormais se connecter avec <strong>{editingEntreprise.email}</strong>.
                        Pensez à cliquer &quot;Sauvegarder + Renvoyer accès&quot; pour notifier la nouvelle adresse.
                      </p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Téléphone</Label>
                    <Input value={editingEntreprise.telephone || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, telephone: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label>Contact</Label>
                    <Input value={editingEntreprise.contact_principal || ''} onChange={e => setEditingEntreprise({ ...editingEntreprise, contact_principal: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 flex-wrap">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-none">Annuler</Button>
                  {editingEntreprise?.user_id && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        await handleEditEntreprise({ preventDefault: () => {} });
                        // Petite pause pour laisser le state se rafraîchir
                        const ent = editingEntreprise;
                        setTimeout(() => handleResendAccess(ent), 600);
                      }}
                      className="rounded-none border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white gap-1"
                      data-testid="edit-save-and-resend"
                    >
                      <PaperPlaneTilt size={14} weight="bold" /> Sauvegarder + Renvoyer accès
                    </Button>
                  )}
                  <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Enregistrer</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Fiche complète */}
        <FicheEntrepriseModal open={ficheOpen} onOpenChange={setFicheOpen} entrepriseId={ficheId} />
      </div>
    </Layout>
  );
}
